import React from 'react';
import { useState, useEffect, useRef } from 'react';
import './ExperienceSection.css';
import ExperienceItem from './ExperienceItem.js';


function ExperienceSection() {

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const experiences = [
      {
          title: "Owner & Founder",
          company: "Beyond Sight Solutions",
          period: "2021 - Present",
          description: "Launched a small business delivering innovative project stewardship solutions to diverse clientele. Engage with business owners to identify strategic and operational gaps, guiding them through development cycles to successfully manage future growth opportunities. Currently focusing on platformization of SaaS and PaaS stewardship products for B2C and B2B markets.",
          achievements: [
            "Developed SaaS solutions for multiple industry verticals",
            "Expanded client base by 40% within the first year",
            "Increased client operational efficiency by an average of 35%"
        ],
        responsibilities: [
            "Identify strategic and operational gaps for client businesses",
            "Guide clients through development cycles & growth strategy",
            "Develop and implement SaaS and PaaS stewardship products"
        ]
      },
      {
          title: "Project Controls Engineer & Analyst",
          company: "Energy Transfer Partner",
          period: "2026 - Present",
          description: "Developed Power BI dashboards for a $5B+ energy pipeline project, integrating cost, schedule, forecast, and operational data. Delivered centralized reporting and performance insights to support executive decision-making.",
          achievements: [
            "Developed 50+ reusable KPIs and performance measures",
            "Centralized reporting across 10+ project functions",
            "Streamlined executive reporting and decision support for a $5B+ project"
        ],
        responsibilities: [
            "Integrate and automate multi-source project data",
            "Establish KPIs and reporting standards for project controls",
            "Deliver actionable insights to support executive decision-making"
        ]
      },
      {
          title: "Technical Project Manager",
          company: "LAZ Parking",
          period: "2024 - 2025",
          description: "Drive cross-functional initiatives to optimize processes and delivery for eCommerce and application development projects. Implement innovative workflows and integrations while supporting scheduling and controls efforts across multiple concurrent projects. Balance technical requirements with business objectives to deliver measurable improvements in operational efficiency.",
          achievements: [
            "Improved developer response times by more than 50%",
            "Enhanced resolution times by over 70%",
            "Successfully managed multiple concurrent technical projects"
        ],
        responsibilities: [
            "Lead cross-functional initiatives for process optimization",
            "Implement efficient workflows and system integrations",
            "Balance technical requirements with business objectives"
        ]
      },
      {
          title: "Product Manager",
          company: "Indeed",
          period: "2021 - 2024",
          description: "Led cross-functional teams of engineers, designers, and operations analysts to improve processes and products for SMB and enterprise platforms. Leveraged data-driven insights and strategic planning to drive improvements in software infrastructure. Spearheaded the development and launch of various in-house products that enhanced user experiences and reduced operational costs.",
          achievements: [
            "Successfully launched multiple in-house products",
            "Tripled production rates & captured $7M in savings annually",
            "Reduced operational turnaround times by 55%"
        ],
        responsibilities: [
            "Lead teams through the product development lifecycle",
            "Analyze data to inform strategic product decisions",
            "Balance technical requirements with business objectives"
        ]
      }
        ,
      {
          title: "Project Controls Engineer",
          company: "ExxonMobil",
          period: "2019 - 2021",
        description: "Strengthened relationships with global contractors by identifying and removing communication barriers, leading to improved engineering support between local and overseas teams. Leveraged Excel and Power BI to track productivity and progress, effectively communicating production delays and ensuring the accuracy of EPC contractor data. Stewarded project budgets on a $2B+ petrochemical expansion, supporting cost and schedule targets through data visualization and trend analysis.",
        achievements: [
            "Maintained critical path milestones on a $2B+ project",
            "Improved coordination between local & overseas teams",
            "Devised  dashboards that significantly reduced reporting times"
        ],
        responsibilities: [
            "Uncover and surface cost and productivity insights",
            "Monitor budgets and communicate reconciliation deviances",
            "Strengthen contractor relationships",
        ]
      }
        ,
      {
          title: "Cost Engineer",
          company: "ExxonMobil",
          period: "2018 - 2019",
          description: "Managed cost estimations and analysis for large-scale petrochemical expansion, identifying cost-saving opportunities before finalizing project budgets. Leveraged Excel and Power BI to produce iterative estimates, enabling senior leadership to understand cost and productivity drivers within projects.",
          achievements: [
            "Identified approximately $500K in savings",
            "Estimated over $900M worth of scope",
            "Enabled leadership to reallocate funds strategically "
        ],
        responsibilities: [
            "Produce and refine cost estimates for global projects",
            "Communicate financial insights to senior leadership",
            "Articulate cost and productivity drivers"
        ]
      }
      ,
      {
          title: "Intern",
          company: "ExxonMobil",
          period: "Summer 2016, Summer 2017",
          description: "Contributed to cross-functional technology projects within a fast-paced corporate environment. Gained hands-on experience in project management methodologies while supporting senior team members in global project initiatives.",
          achievements: [
              "Developed forecasting tools for material and labor costs. ",
              "Developed a tool for efficient iterative cost reconciliation.",
              "Highlighted cost deviation factors "
          ],
          responsibilities: [
              "Gather requirements for requested tools",
              "Document tools features and use cases",
              "Formalize user guides" 
              ]
      }
        ,
      {
          title: "Student, Tutor, Researcher",
          company: "Xavier University of Louisiana & Lousisiana State University",
          period: "2012 - 2017",
          description: "Worked as a tutor and undergraduate researcher while pursuing a double bachelor's degree in Physics and Civil Engineering, demonstrating strong analytical and problem-solving skills across multiple scientific disciplines. ",
          achievements: [
              "Cum Laude & Summa Cum Laude",
              "Facilitated understanding of math and physics concepts",
              "Fostered interest in STEM among youth"
          ],
          responsibilities: [
              "Maintain comprehension of disciplinary concepts",
              "Lead review sessions and proctoring exams",
              "Analyze experimental data"
          ]
      }
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);
  return (
    <section id="experience" className="py-20 bg-white" ref={sectionRef}>
        <div className="max-w-6xl mx-auto px-6">
            <div className={`text-center mb-16 fade-in ${isVisible ? 'visible' : ''}`}>
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Professional Experience</h2>
                <p className="text-xl text-gray-600">Career progression in project management and leadership</p>
            </div>

            <div className="max-w-4xl mx-auto">
                <div className="space-y-8">
                    {experiences.map((experience, index) => (
                        <ExperienceItem 
                            key={index}
                            experience={experience}
                            isVisible={isVisible}
                        />
                    ))}
                </div>
            </div>
        </div>
    </section>
  );
}

export default ExperienceSection;